import * as THREE from 'three'
import type { Bot } from 'mineflayer'
import { inventoryDefinition, nativeInventorySlotAt } from './xrInventoryLayout'
import { XRInventoryCanvas, type XRInventoryAssets } from './xrInventoryCanvas'
import { itemLabel, flattenText } from './xrText'
import { XRHandRig, faceHandPanel } from './xrHandRig'

/** A real protocol inventory: slots, cursor stack and crafting results come from the server. */
export class XRInventory {
  readonly canvas = document.createElement('canvas')
  readonly texture: THREE.CanvasTexture
  readonly panel: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>
  readonly hudCanvas = document.createElement('canvas')
  readonly hudTexture: THREE.CanvasTexture
  readonly hud: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>
  private context: CanvasRenderingContext2D
  private hudContext: CanvasRenderingContext2D
  private ray = new THREE.Raycaster()
  private selected: number | null = null
  private nativeCanvas: XRInventoryCanvas
  private busy = false
  private error = ''
  private lastPaint = 0
  private lastServerClose = -Infinity
  private hasPose = false
  private hands?: XRHandRig
  private suppressed = false
  private handGeometry = new THREE.PlaneGeometry(0.44, 0.44 * 720 / 1024)
  private fallbackGeometry = new THREE.PlaneGeometry(0.62, 0.62 * 96 / 1024)
  private readonly onWindowOpen = () => this.open(performance.now() - this.lastServerClose < 500)
  private readonly onWindowClose = () => { this.lastServerClose = performance.now(); this.panel.visible = false; this.updateHandHUD() }

  constructor(private readonly bot: Bot, scene: THREE.Scene, private readonly camera: THREE.PerspectiveCamera, assets?: XRInventoryAssets, private readonly locomotion: () => 'teleport' | 'walk' = () => 'walk', private readonly teleportStatus?: () => string) {
    this.nativeCanvas = new XRInventoryCanvas(assets)
    this.canvas.width = 1024
    this.canvas.height = 1100
    this.context = this.canvas.getContext('2d')!
    this.texture = new THREE.CanvasTexture(this.canvas)
    this.texture.colorSpace = THREE.SRGBColorSpace
    this.panel = new THREE.Mesh(new THREE.PlaneGeometry(1.5, 1.5 * 1100 / 1024), new THREE.MeshBasicMaterial({ map: this.texture, transparent: true, depthTest: false, depthWrite: false }))
    this.panel.renderOrder = 10000
    this.panel.visible = false
    scene.add(this.panel)
    this.hudCanvas.width = 1024
    this.hudCanvas.height = 720
    this.hudContext = this.hudCanvas.getContext('2d')!
    this.hudTexture = new THREE.CanvasTexture(this.hudCanvas)
    this.hudTexture.colorSpace = THREE.SRGBColorSpace
    const uv = this.fallbackGeometry.attributes.uv
    for (let i = 0; i < uv.count; i++) uv.setY(i, 1 - 96 / 720 + uv.getY(i) * 96 / 720)
    this.hud = new THREE.Mesh(this.fallbackGeometry, new THREE.MeshBasicMaterial({ map: this.hudTexture, transparent: true, depthTest: false, depthWrite: false }))
    this.hud.position.set(0, -0.32, -1)
    this.hud.renderOrder = 10001
    camera.add(this.hud)
    bot.on('windowOpen', this.onWindowOpen)
    bot.on('windowClose', this.onWindowClose)
  }

  get isOpen() { return this.panel.visible }

  attachHands(hands: XRHandRig) { this.hands = hands; this.updateHandHUD() }
  setUiSuppressed(suppressed: boolean) { this.suppressed = suppressed; this.updateHandHUD() }

  private updateHandHUD() {
    const grip = this.hands?.rightTracked ? this.hands.rightGrip : undefined
    const parent = grip ?? this.camera
    if (this.hud.parent !== parent) parent.add(this.hud)
    this.hud.visible = !this.isOpen && !this.suppressed
    this.hud.geometry = grip ? this.handGeometry : this.fallbackGeometry
    if (grip) {
      this.hud.position.set(0.17, 0.16, -0.04)
      faceHandPanel(this.hud, this.camera)
    } else { this.hud.position.set(0, -0.32, -1); this.hud.quaternion.identity() }
  }

  open(preservePose = false) {
    const alreadyOpen = this.panel.visible
    this.panel.visible = true
    this.hud.visible = false
    this.error = ''
    // Keep the menu fixed while the player looks around it to select cells.
    if (!alreadyOpen && (!preservePose || !this.hasPose)) {
      this.camera.getWorldPosition(this.panel.position)
      this.camera.getWorldQuaternion(this.panel.quaternion)
      this.panel.position.add(new THREE.Vector3(0, 0, -1.6).applyQuaternion(this.panel.quaternion))
      this.panel.updateMatrixWorld(true)
      this.hasPose = true
    }
    this.paint()
  }

  close() {
    if (this.bot.currentWindow) this.bot.closeWindow(this.bot.currentWindow)
    else {
      // Closing window 0 is also important: vanilla returns items left in the crafting grid.
      this.bot.closeWindow(this.bot.inventory)
    }
    this.panel.visible = false
    this.updateHandHUD()
  }

  toggle() { if (this.isOpen) this.close(); else this.open() }

  async click(rightClick: boolean, quickMove: boolean) {
    if (this.selected === null || this.busy) return
    this.busy = true
    try {
      await this.bot.clickWindow(this.selected, rightClick ? 1 : 0, quickMove ? 1 : 0)
      this.error = ''
    } catch (error) {
      this.error = error instanceof Error ? error.message : String(error)
    } finally {
      this.busy = false
      this.paint()
    }
  }

  update(origin: THREE.Vector3, direction: THREE.Vector3, time: number) {
    this.updateHandHUD()
    if (this.isOpen) {
      this.ray.set(origin, direction)
      const hit = this.ray.intersectObject(this.panel)[0]
      const window = this.bot.currentWindow ?? this.bot.inventory
      const selected = hit?.uv ? nativeInventorySlotAt(hit.uv.x * this.canvas.width, (1 - hit.uv.y) * this.canvas.height, inventoryDefinition(this.bot.currentWindow)) : null
      if (selected !== this.selected) { this.selected = selected; this.paint() }
    }
    if (time - this.lastPaint > 100) { this.paint(); this.lastPaint = time }
  }

  private paint() {
    const ctx = this.context
    if (this.isOpen) {
      const win = this.bot.currentWindow ?? this.bot.inventory
      ctx.clearRect(0, 0, 1024, 1100)
      ctx.fillStyle = 'rgba(13, 23, 29, 0.97)'
      ctx.fillRect(0, 0, 1024, 1100)
      ctx.fillStyle = '#eafff2'
      ctx.font = 'bold 34px sans-serif'
      const titleFonts = flattenText(this.bot.currentWindow?.title).map(run => run.font)
      const screenName = titleFonts.includes('jerpg:phone') ? 'Phone' : titleFonts.includes('jerpg:massage') ? 'Massage' : inventoryDefinition(this.bot.currentWindow).title
      ctx.fillText(`${screenName} · Health ${Math.ceil(this.bot.health)} · Food ${this.bot.food}`, 44, 52, 940)
      ctx.font = '22px sans-serif'
      ctx.fillText('Point at a slot · Right trigger: pick/place · Right grip: split/place one', 44, 92, 940)
      ctx.fillText('Hold left grip + trigger: quick move · B / Y: close', 44, 116, 940)
      this.nativeCanvas.paint(ctx, win, this.bot.currentWindow, this.selected)
      const cursor = win.selectedItem
      const item = this.selected === null ? null : win.slots[this.selected]
      ctx.fillStyle = '#eafff2'
      ctx.font = '24px sans-serif'
      ctx.fillText(`Holding: ${cursor ? `${itemLabel(cursor)} ×${cursor.count}` : 'empty'}${this.busy ? ' · syncing…' : ''}`, 44, 1020, 936)
      ctx.fillText(this.error || (item ? `Selected: ${itemLabel(item)} ×${item.count}` : 'Point at an item or button to select it.'), 44, 1050, 936)
      this.texture.needsUpdate = true
    }
    const hud = this.hudContext
    hud.clearRect(0, 0, 1024, 720)
    hud.fillStyle = 'rgba(9, 25, 20, 0.94)'
    hud.fillRect(0, 0, 1024, 720)
    hud.strokeStyle = '#78b594'; hud.lineWidth = 3; hud.strokeRect(2, 2, 1020, 716)
    hud.fillStyle = '#eaffed'; hud.font = 'bold 34px sans-serif'
    hud.fillText(`Health ${Math.ceil(this.bot.health)}/20  ·  Food ${this.bot.food}/20  ·  XP ${this.bot.experience?.level ?? 0}`, 23, 43, 978)
    hud.font = '29px sans-serif'
    hud.fillText(`${this.bot.quickBarSlot + 1}  ${itemLabel(this.bot.heldItem)}`, 23, 82, 978)
    for (let slot = 0; slot < 9; slot++) {
      const x = 20 + (slot % 3) * 330
      const y = 108 + Math.floor(slot / 3) * 170
      const item = this.bot.inventory.slots[36 + slot]
      hud.fillStyle = slot === this.bot.quickBarSlot ? '#315f46' : '#192f28'
      hud.fillRect(x, y, 314, 155)
      if (slot === this.bot.quickBarSlot) { hud.strokeStyle = '#b4ffd0'; hud.lineWidth = 4; hud.strokeRect(x, y, 314, 155) }
      hud.fillStyle = '#fff'; hud.font = '26px sans-serif'
      hud.fillText(`${slot + 1}  ${item ? itemLabel(item) : 'Empty'}`, x + 12, y + 34, 289)
      this.nativeCanvas.drawItemIcon(hud, item, x + 14, y + 51, 88)
      if (item) { hud.font = 'bold 31px sans-serif'; hud.fillText(`×${item.count}`, x + 125, y + 107, 168) }
    }
    hud.fillStyle = '#c6dece'; hud.font = '24px sans-serif'
    hud.fillText(this.locomotion() === 'teleport' ? (this.teleportStatus?.() ?? 'Left trigger: aim and teleport') : 'Left stick: walk · A: jump', 22, 652, 980)
    hud.fillText('Right stick ↑↓: select · B/Y: inventory · X: menu', 22, 686, 980)
    this.hudTexture.needsUpdate = true
  }

  dispose() {
    this.bot.removeListener('windowOpen', this.onWindowOpen)
    this.bot.removeListener('windowClose', this.onWindowClose)
    this.panel.removeFromParent()
    this.hud.removeFromParent()
    this.panel.geometry.dispose(); this.panel.material.dispose(); this.texture.dispose()
    this.handGeometry.dispose(); this.fallbackGeometry.dispose(); this.hud.material.dispose(); this.hudTexture.dispose()
  }
}
